import PageWrapper from 'hoc/PageWrapper'
import {
  Box,
  Button,
  Divider,
  IconButton,
  styled,
  tableCellClasses,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { CreateContractDialog } from 'pages/contracts/components/CreateContractDialog'
import { useState } from 'react'
import { useContractsQuery, usePublishContractMutation } from 'services/contracts'
import { Loading } from 'components/Loading'
import { ErrorElement } from 'components/Error'
import { TablePagination } from 'components/TablePagination'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import { EditContractDialog } from 'pages/contracts/components/EditContractDialog'
import { EContractStatus, IContractItem } from 'types/contracts'
import { Edit, Publish, RemoveRedEyeOutlined } from '@mui/icons-material'
import { PublishContractForm } from 'pages/contracts/components/PublishContractForm'
import { useUserCurrentQuery } from 'services/users'
import { EOrganizationType } from 'types/users'
import { toast } from 'react-toastify'

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}))

export function Contracts() {
  const { t } = useTranslation()
  const [isCreateOpen, setCreateOpen] = useState(false)
  const [page, setPage] = useState(0)
  const [editingItem, setEditingItem] = useState<IContractItem>()
  const [publishingItem, setPublishingItem] = useState<IContractItem>()
  const { data, isLoading, isError } = useContractsQuery({ page, size: 10 })
  const [publishContract, { isLoading: isPublishLoading }] =
    usePublishContractMutation()
  const { data: currentUser } = useUserCurrentQuery()

  const canEdit =
    currentUser?.organizationType === EOrganizationType.TENDER_AUTHORITY

  const handleItemPublish = () => {
    if (publishingItem) {
      publishContract({ id: publishingItem.id }).then((res) => {
        if ('error' in res) {
          toast.error(t('failedToCreate'))
        } else {
          toast.success(t('operation-success'))
          setPublishingItem(undefined)
        }
      })
    }
  }

  const mapItemStatus = (status: EContractStatus) => {
    if (status === EContractStatus.PUBLISHED) {
      return t('PUBLISHED')
    }

    return t('status-CREATED')
  }

  return (
    <PageWrapper>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography fontWeight="bold" variant="h6">
          {t('contracts')}
        </Typography>
        {canEdit && (
          <Button
            color="primary"
            variant="contained"
            style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
            onClick={() => setCreateOpen(true)}
          >
            {t('create')}
          </Button>
        )}
      </Box>
      <Divider sx={{ my: 1.5 }} />
      {isLoading ? (
        <Loading fullPage={false} />
      ) : data ? (
        <>
          <Box>
            <TableContainer component={Box}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <StyledTableCell>{t('contract-number')}</StyledTableCell>
                    <StyledTableCell>{t('contract-amount')}</StyledTableCell>
                    <StyledTableCell>{t('contract-lot-name')}</StyledTableCell>
                    <StyledTableCell>
                      {t('contractorOrganizationName')}
                    </StyledTableCell>
                    <StyledTableCell>{t('status')}</StyledTableCell>
                    <StyledTableCell width={64} />
                    {canEdit && <StyledTableCell width={64} />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.items.map((item) => {
                    const notPublished = item.status !== EContractStatus.PUBLISHED

                    return (
                      <TableRow key={item.id}>
                        <TableCell scope="row">{item.contractNumber}</TableCell>
                        <TableCell scope="row">{item.amount}</TableCell>
                        <TableCell scope="row">{item.lotName}</TableCell>
                        <TableCell scope="row">
                          {item.contractorOrganizationName}
                        </TableCell>
                        <TableCell scope="row">
                          {mapItemStatus(item.status)}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => setEditingItem(item)}
                          >
                            {notPublished && canEdit ? (
                              <Edit />
                            ) : (
                              <RemoveRedEyeOutlined />
                            )}
                          </IconButton>
                        </TableCell>
                        {canEdit && (
                          <TableCell scope="row">
                            {notPublished && (
                              <IconButton
                                size="small"
                                disabled={isPublishLoading}
                                color="primary"
                                onClick={() => setPublishingItem(item)}
                              >
                                <Publish />
                              </IconButton>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          {data.info?.totalRows > 0 && (
            <TablePagination
              total={data.info.totalRows}
              rowsPerPage={10}
              page={page}
              onPageChange={setPage}
            />
          )}
        </>
      ) : isError ? (
        <ErrorElement />
      ) : null}
      {isCreateOpen && (
        <CreateContractDialog
          isOpen={isCreateOpen}
          onClose={() => setCreateOpen(false)}
        />
      )}
      {editingItem && (
        <EditContractDialog
          canEdit={canEdit}
          isOpen
          onClose={() => setEditingItem(undefined)}
          contractId={editingItem.id}
        />
      )}
      {publishingItem && (
        <PublishContractForm
          isOpen
          onClose={() => setPublishingItem(undefined)}
          contract={publishingItem}
          onPublish={handleItemPublish}
        />
      )}
    </PageWrapper>
  )
}
